import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Minus, Flame, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { AdminSheet } from "@/components/admin/AdminSheet";
import { MemberPanel } from "@/components/admin/MemberReview";
import { SEATS, normalizeSeat } from "@/lib/desk/career";
import { cn } from "@/lib/utils";
import ExampleMemberCard from "@/components/coach/ExampleMemberCard";
import { useAdminRole } from "@/hooks/useAdminRole";

interface G {
  user_id: string; full_name: string | null; email: string | null; photo: string | null; stage: number; seat: string | null;
  academy_lessons: number; badges: number; workout_badges: number; streak: number; last_active: string | null;
  musts_week: number; missed_week?: number; confirmed_week?: number; fitness_delta: number | null; money_delta: number | null; hw_waiting?: boolean;
}
type Key = "name" | "stage" | "seat" | "academy_lessons" | "badges" | "streak" | "last_active" | "musts_week" | "fitness_delta" | "money_delta";
const SEAT_ORDER = ["intern", "analyst", "trader", "senior_trader"];
const days = (d: string | null) => (d ? Math.floor((Date.now() - new Date(d).getTime()) / 864e5) : 9999);

function Trend({ v, goodUp }: { v: number | null; goodUp: boolean }) {
  if (v == null || v === 0) return <Minus className="mx-auto h-4 w-4 text-muted-foreground" />;
  const good = goodUp ? v > 0 : v < 0;
  const I = v > 0 ? ArrowUp : ArrowDown;
  return <I className={cn("mx-auto h-4 w-4", good ? "text-emerald-700" : "text-destructive")} />;
}

export default function CoachGroupTable({ coachMode = false }: { coachMode?: boolean }) {
  const { data: isAdmin } = useAdminRole();
  const { data = [], isLoading, error } = useQuery({
    queryKey: ["coach-group-overview"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("coach_group_overview");
      if (error) throw error;
      const since = new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10);
      const { data: ms } = await supabase.from("schedule_steps").select("user_id").eq("status", "missed").gte("day", since);
      const cnt = new Map<string, number>();
      (ms ?? []).forEach((m) => cnt.set(m.user_id, (cnt.get(m.user_id) ?? 0) + 1));
      const { data: cf } = await supabase.from("day_overrides").select("user_id").gte("day", since);
      const ccnt = new Map<string, number>();
      (cf ?? []).forEach((m) => ccnt.set(m.user_id, (ccnt.get(m.user_id) ?? 0) + 1));
      const { data: hw } = await (supabase as any).from("academy_homework_submissions").select("user_id").is("graded_at", null);
      const hset = new Set((hw ?? []).map((h: any) => h.user_id));
      return (data as G[]).map((r) => ({ ...r, missed_week: cnt.get(r.user_id) ?? 0, confirmed_week: ccnt.get(r.user_id) ?? 0, hw_waiting: hset.has(r.user_id) }));
    },
  });
  const [q, setQ] = useState("");
  const [seat, setSeat] = useState<string>("all");
  const [inactive, setInactive] = useState(false);
  const [sort, setSort] = useState<{ k: Key; asc: boolean }>({ k: "name", asc: true });
  const [open, setOpen] = useState<G | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const rows = useMemo(() => {
    const val = (r: G, k: Key): number | string => {
      if (k === "name") return (r.full_name || r.email || "").toLowerCase();
      if (k === "seat") return r.seat ? SEAT_ORDER.indexOf(normalizeSeat(r.seat)) : -1;
      if (k === "last_active") return -days(r.last_active);
      if (k === "badges") return r.badges + r.workout_badges;
      return (r[k] as number | null) ?? -Infinity;
    };
    return data
      .filter((r) => !q || `${r.full_name} ${r.email}`.toLowerCase().includes(q.toLowerCase()))
      .filter((r) => seat === "all" || (seat === "none" ? !r.seat : r.seat && normalizeSeat(r.seat) === seat))
      .filter((r) => !inactive || days(r.last_active) >= 7)
      .sort((a, b) => { const x = val(a, sort.k), y = val(b, sort.k); return (x < y ? -1 : x > y ? 1 : 0) * (sort.asc ? 1 : -1); });
  }, [data, q, seat, inactive, sort]);

  const H = ({ k, children }: { k: Key; children: React.ReactNode }) => (
    <th className="cursor-pointer select-none px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => setSort((s) => ({ k, asc: s.k === k ? !s.asc : true }))}>
      {children}{sort.k === k ? (sort.asc ? " ▲" : " ▼") : ""}
    </th>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between md:hidden">
        <span className="text-xs text-muted-foreground">{rows.length} members</span>
        <button onClick={() => setShowFilters((v) => !v)} className="text-xs text-gold-dark underline-offset-2 hover:underline">{showFilters ? "Hide search" : "Search & filter"}</button>
      </div>
      <div className={cn("flex-wrap items-center gap-2", showFilters ? "flex" : "hidden md:flex")}>
        <Input placeholder="Search by name" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-56" />
        {["all", ...SEAT_ORDER, "none"].map((s) => (
          <button key={s} onClick={() => setSeat(s)} className={cn("h-8 rounded-full border px-3 text-xs", seat === s ? "border-gold bg-gold/15" : "border-border")}>
            {s === "all" ? "All" : s === "none" ? "Not in Simulation" : SEATS[s as keyof typeof SEATS]?.title ?? s}
          </button>
        ))}
        <button onClick={() => setInactive((v) => !v)} className={cn("h-8 rounded-full border px-3 text-xs", inactive ? "border-destructive bg-destructive/10" : "border-border")}>Inactive 7+ days</button>
        <span className="ml-auto text-xs text-muted-foreground">{rows.length} members</span>
      </div>
      {error && <p className="text-sm text-destructive">Couldn't load the group.</p>}
      {isAdmin && <ExampleMemberCard />}
      <div className="space-y-2 md:hidden">
        {isLoading && <p className="p-4 text-center text-muted-foreground">Loading…</p>}
        {rows.map((r) => {
          const d = days(r.last_active);
          const dot = d >= 7 || r.hw_waiting ? "bg-destructive" : d >= 3 ? "bg-gold" : "bg-emerald-600";
          return (
            <button key={r.user_id} onClick={() => setOpen(r)} className="flex w-full items-center gap-3 rounded-xl border border-gold/30 bg-card/80 p-3 text-left shadow-sm">
              {r.photo ? <img src={r.photo} alt="" className="h-14 w-14 rounded-full object-cover ring-2 ring-gold/50" /> : <div className="h-14 w-14 rounded-full bg-muted ring-2 ring-gold/30" />}
              <div className="min-w-0 flex-1">
                <div className="truncate font-display text-lg text-foreground">{r.full_name || r.email || "Student"}</div>
                <div className="text-xs text-muted-foreground">Stage {r.stage || 1}{r.seat ? ` · ${SEATS[normalizeSeat(r.seat)]?.title}` : ""}</div>
                <div className="mt-0.5 flex items-center gap-1 text-xs text-foreground/80"><Flame className="h-3 w-3 text-gold" />{r.streak} days · {d === 9999 ? "never active" : d === 0 ? "active today" : `${d}d ago`}</div>
                {r.hw_waiting && <span className="mt-1 inline-block rounded-full bg-gold px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">Homework waiting</span>}
              </div>
              <div className="flex shrink-0 flex-col items-center gap-4 self-stretch">
                <span className={cn("h-3 w-3 rounded-full", dot)} />
                <ChevronRight className="h-5 w-5 text-gold-dark" />
              </div>
            </button>
          );
        })}
      </div>
      <div className="hidden w-full overflow-x-auto rounded-xl border border-border bg-card/60 md:block">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="border-b border-border"><tr>
            <H k="name">Member</H><H k="stage">Stage</H><H k="seat">Simulation</H><H k="academy_lessons">Academy</H><H k="badges">Badges</H>
            <H k="streak">Streak</H><H k="last_active">Last active</H><H k="musts_week">Musts this week</H><th className="px-2 py-2 text-left">Missed (7d)</th><th className="px-2 py-2 text-left">Confirmed tomorrow (7d)</th><H k="fitness_delta">Fitness</H><H k="money_delta">Savings</H>
          </tr></thead>
          <tbody>
            {isLoading && <tr><td colSpan={10} className="p-4 text-center text-muted-foreground">Loading…</td></tr>}
            {rows.map((r) => {
              const d = days(r.last_active);
              return (
                <tr key={r.user_id} onClick={() => setOpen(r)} className="cursor-pointer border-b border-border/50 hover:bg-gold/5">
                  <td className="px-2 py-2"><div className="flex items-center gap-2">
                    {r.photo ? <img src={r.photo} alt="" className="h-8 w-8 rounded-full object-cover" /> : <div className="h-8 w-8 rounded-full bg-muted" />}
                    <span className="font-medium">{r.full_name || r.email || "Student"}</span>
                  </div></td>
                  <td className="px-2 py-2">{r.stage || "—"}</td>
                  <td className="px-2 py-2">{r.seat ? SEATS[normalizeSeat(r.seat)]?.title : "—"}</td>
                  <td className="px-2 py-2">{r.academy_lessons} lessons</td>
                  <td className="px-2 py-2">{r.badges + r.workout_badges}</td>
                  <td className="px-2 py-2">{r.streak}d</td>
                  <td className={cn("px-2 py-2", d >= 7 && "font-semibold text-destructive")}>{d === 9999 ? "never" : d === 0 ? "today" : `${d}d ago`}</td>
                  <td className="px-2 py-2">{r.musts_week}</td><td className="px-2 py-2">{r.missed_week ?? 0}</td><td className="px-2 py-2">{r.confirmed_week ?? 0}</td>
                  <td className="px-2 py-2"><Trend v={r.fitness_delta} goodUp={false} /></td>
                  <td className="px-2 py-2"><Trend v={r.money_delta} goodUp /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <AdminSheet open={!!open} onOpenChange={(v) => !v && setOpen(null)} title="Member Review" description={open?.email ?? undefined} size="md">
        {open && <MemberPanel coachMode={coachMode} row={{ user_id: open.user_id, name: open.full_name || open.email || "Student", email: open.email, gender: null, stage: open.stage, lastLog: open.last_active, chapters: 0 }} />}
      </AdminSheet>
    </div>
  );
}
